/**
 * Machine-readable failure reasons (`error.code`), mirroring the backend's common/constants/error-codes.ts, plus two
 * the client sets itself (NETWORK, BAD_RESPONSE). Code branches on these, never on the (Indonesian) message text.
 */
export const ERROR_CODE = {
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  IN_USE: 'IN_USE',
  INVALID_REFERENCE: 'INVALID_REFERENCE',
  BUSY_RETRY: 'BUSY_RETRY',
  SEARCH_TOO_BROAD: 'SEARCH_TOO_BROAD',
  /** the caller's permissions lack the route's */
  PERMISSION_REQUIRED: 'PERMISSION_REQUIRED',
  /** a province, location or object outside the caller's scope (lists, mutations); by-id reads answer 404 instead */
  OUT_OF_SCOPE: 'OUT_OF_SCOPE',
  /** a statistics region with fewer than 5 registered households, asked for directly by a caller without pii.read */
  STATISTICS_SUPPRESSED: 'STATISTICS_SUPPRESSED',
  /** a courier's cash of that day is recorded already (correct it instead) */
  CASH_HANDOVER_EXISTS: 'CASH_HANDOVER_EXISTS',
  /** a Hub has an open stock count already */
  STOCK_COUNT_OPEN: 'STOCK_COUNT_OPEN',
  /** a role and scope that do not go together (an operator without a location, an admin at a location) */
  SCOPE_INVALID: 'SCOPE_INVALID',
  /** a province admin granting national access, another province, or the admin role */
  SCOPE_TOO_WIDE: 'SCOPE_TOO_WIDE',
  LOGIN_FAILED: 'LOGIN_FAILED',
  LOGIN_RATE_LIMITED: 'LOGIN_RATE_LIMITED',
  PASSWORD_RATE_LIMITED: 'PASSWORD_RATE_LIMITED',
  WRONG_PASSWORD: 'WRONG_PASSWORD',
  /** two-step verification: a wrong, expired or reused code (sign-in, enrollment, disabling, new recovery codes) */
  TOTP_INVALID: 'TOTP_INVALID',
  /** the sign-in's second step is gone (expired, used, too many wrong codes): sign in with the password again */
  LOGIN_STEP_EXPIRED: 'LOGIN_STEP_EXPIRED',
  /** the account needs a second step and the client did not say it can ask for it (an older dashboard) */
  LOGIN_CLIENT_OUTDATED: 'LOGIN_CLIENT_OUTDATED',
  TOTP_ALREADY_ENABLED: 'TOTP_ALREADY_ENABLED',
  TOTP_NOT_ENABLED: 'TOTP_NOT_ENABLED',
  TOTP_SETUP_REQUIRED: 'TOTP_SETUP_REQUIRED',
  /** the account must keep two-step verification (a national admin) */
  TOTP_REQUIRED: 'TOTP_REQUIRED',
  /** the server cannot use two-step verification (no encryption key configured) */
  TOTP_UNAVAILABLE: 'TOTP_UNAVAILABLE',
  PASSWORD_UNCHANGED: 'PASSWORD_UNCHANGED',
  PROVINCE_UNKNOWN: 'PROVINCE_UNKNOWN',
  /** a hub needs its village (desa / kelurahan) */
  VILLAGE_REQUIRED: 'VILLAGE_REQUIRED',
  /** a village code the database does not have (or the villages are not loaded yet) */
  VILLAGE_UNKNOWN: 'VILLAGE_UNKNOWN',
  /** the village lies in another regency than the location's */
  VILLAGE_OUTSIDE_REGENCY: 'VILLAGE_OUTSIDE_REGENCY',
  LOCATION_REQUIRED: 'LOCATION_REQUIRED',
  LAST_ADMIN: 'LAST_ADMIN',
  LOCATION_HAS_OPERATORS: 'LOCATION_HAS_OPERATORS',
  LOCATION_HAS_GATEWAYS: 'LOCATION_HAS_GATEWAYS',
  LOCATION_HAS_STOCK: 'LOCATION_HAS_STOCK',
  LOCATION_HAS_HISTORY: 'LOCATION_HAS_HISTORY',
  /** the distribution network: a parent of the wrong type, or a required parent missing */
  PARENT_INVALID: 'PARENT_INVALID',
  LOCATION_HAS_CHILDREN: 'LOCATION_HAS_CHILDREN',
  /** a location's point outside Indonesia's bounding box */
  COORDINATES_OUTSIDE_INDONESIA: 'COORDINATES_OUTSIDE_INDONESIA',
  OWNER_WAREHOUSE_EXISTS: 'OWNER_WAREHOUSE_EXISTS',
  OWNER_WAREHOUSE_REQUIRED: 'OWNER_WAREHOUSE_REQUIRED',
  GATEWAY_ONLINE: 'GATEWAY_ONLINE',
  GATEWAY_OFFLINE: 'GATEWAY_OFFLINE',
  BROKER_UNAVAILABLE: 'BROKER_UNAVAILABLE',
  FILTER_REQUIRED: 'FILTER_REQUIRED',
  OTA_RELEASE_IN_USE: 'OTA_RELEASE_IN_USE',
  /** a rollout to an older version than a gateway runs, without allowDowngrade */
  OTA_DOWNGRADE: 'OTA_DOWNGRADE',
  REGISTER_AT_WAREHOUSE_ONLY: 'REGISTER_AT_WAREHOUSE_ONLY',
  /** a delete of a cylinder that was ever put on a Surat Jalan or moved: only registration mistakes are deleted */
  CYLINDER_IN_HISTORY: 'CYLINDER_IN_HISTORY',
  /** a change refused while the cylinder travels, did not arrive, or is on a Surat Jalan being loaded (status, Ganti tag) */
  CYLINDER_IN_TRANSIT: 'CYLINDER_IN_TRANSIT',
  CYLINDER_MISSING: 'CYLINDER_MISSING',
  CYLINDER_ON_SHIPMENT: 'CYLINDER_ON_SHIPMENT',
  // a tag is bound to a cylinder once (registration, Ganti tag)
  /** the EPC or the chip's TID was bound to a cylinder before (in force or retired); the message names the cylinder */
  TAG_ALREADY_USED: 'TAG_ALREADY_USED',
  /** the Reader has not read the chip's full TID yet */
  TAG_TID_MISSING: 'TAG_TID_MISSING',
  /** the tag is no longer read live by an online Reader */
  TAG_READ_STALE: 'TAG_READ_STALE',
  /** Ganti tag: the new tag is not read at the cylinder's location; the message names the place */
  TAG_READ_ELSEWHERE: 'TAG_READ_ELSEWHERE',
  /** the TID sent is not the one the Reader read for this EPC (an older client) */
  TAG_TID_MISMATCH: 'TAG_TID_MISMATCH',
  /** a household holds the cylinder: its status is not changed from the dashboard */
  CYLINDER_WITH_CONSUMER: 'CYLINDER_WITH_CONSUMER',
  /** the cylinder was destroyed (final): nothing about it changes any more */
  CYLINDER_DESTROYED: 'CYLINDER_DESTROYED',
  // test results (Bengkel Uji)
  /** only an operator of the Bengkel Uji where the cylinder is records its result */
  TEST_WORKSHOP_ONLY: 'TEST_WORKSHOP_ONLY',
  /** the cylinder did not arrive at the workshop on a Surat Jalan */
  TEST_NO_SESSION: 'TEST_NO_SESSION',
  /** the cylinder's status does not allow a test (lost, destroyed) */
  TEST_NOT_ALLOWED: 'TEST_NOT_ALLOWED',
  /** a different result was already recorded for this arrival */
  TEST_ALREADY_RECORDED: 'TEST_ALREADY_RECORDED',
  CYLINDER_NOT_MISSING: 'CYLINDER_NOT_MISSING',
  CYLINDER_NOT_SEEN: 'CYLINDER_NOT_SEEN',
  SHIPMENT_STATUS: 'SHIPMENT_STATUS',
  SHIPMENT_EMPTY: 'SHIPMENT_EMPTY',
  NOTE_REQUIRED: 'NOTE_REQUIRED',
  SCAN_SESSION_BUSY: 'SCAN_SESSION_BUSY',
  UNLOAD_NOT_STARTED: 'UNLOAD_NOT_STARTED',
  LOAD_REJECTED: 'LOAD_REJECTED',
  NOT_ON_MANIFEST: 'NOT_ON_MANIFEST',
  ALREADY_ON_MANIFEST: 'ALREADY_ON_MANIFEST',
  ALREADY_RECEIVED: 'ALREADY_RECEIVED',
  /** this kind of Surat Jalan cannot go from the origin's type to the destination's (the route matrix) */
  ROUTE_NOT_ALLOWED: 'ROUTE_NOT_ALLOWED',
  /** a withdrawal reason on a distribution note, or a withdrawal without one */
  WITHDRAWAL_REASON_INVALID: 'WITHDRAWAL_REASON_INVALID',
  /** location photos: not a readable JPEG/PNG/WebP, the same picture twice, the most a location may have */
  PHOTO_INVALID: 'PHOTO_INVALID',
  PHOTO_DUPLICATE: 'PHOTO_DUPLICATE',
  PHOTO_LIMIT: 'PHOTO_LIMIT',
  // Hub registration (Phase 2), mirroring the backend's codes
  /** the server has no PII keys configured: Hub registration cannot take personal data on it */
  HUB_REGISTRATION_UNAVAILABLE: 'HUB_REGISTRATION_UNAVAILABLE',
  /** the application changed since it was read (someone else acted): reload, then decide again */
  STALE_VERSION: 'STALE_VERSION',
  /** the action is not allowed in the application's current state */
  HUB_APPLICATION_STATE: 'HUB_APPLICATION_STATE',
  /** the person in charge (by NIK) already has an open application (the message names nothing about it) */
  HUB_APPLICATION_DUPLICATE: 'HUB_APPLICATION_DUPLICATE',
  /** a required document of the applicant type is missing, or one was given twice */
  HUB_DOCUMENTS_INCOMPLETE: 'HUB_DOCUMENTS_INCOMPLETE',
  /** an upload id that is unknown, expired (24 h), already used, or of a kind the step does not take */
  HUB_UPLOAD_INVALID: 'HUB_UPLOAD_INVALID',
  /** the file is not one of its kind's formats (by content), or cannot be read */
  HUB_FILE_INVALID: 'HUB_FILE_INVALID',
  /** the resubmission changed something the revision did not ask for, or left out what it asked for */
  HUB_REVISION_MISMATCH: 'HUB_REVISION_MISMATCH',
  /** the revision limit is reached: only a rejection is left */
  HUB_REVISION_LIMIT: 'HUB_REVISION_LIMIT',
  /** checking, recommending and approving one application are different people */
  SEPARATION_OF_DUTIES: 'SEPARATION_OF_DUTIES',
  /** the visit checklist is incomplete or inconsistent (a mandatory item without a photo, ...) */
  CHECKLIST_INVALID: 'CHECKLIST_INVALID',
  /** the device position is not precise enough */
  GPS_INACCURATE: 'GPS_INACCURATE',
  /** the visit failed: only a revision request or a rejection follows */
  VISIT_FAILED: 'VISIT_FAILED',
  VERIFIER_INVALID: 'VERIFIER_INVALID',
  SUPPLIER_INVALID: 'SUPPLIER_INVALID',
  /** the Reader is unknown, placed elsewhere, offline or not reading */
  COMMISSIONING_FAILED: 'COMMISSIONING_FAILED',
  HUB_OPERATOR_EMAIL_TAKEN: 'HUB_OPERATOR_EMAIL_TAKEN',
  /** the document needs personal-data access */
  DOCUMENT_RESTRICTED: 'DOCUMENT_RESTRICTED',
  LINK_INVALID: 'LINK_INVALID',
  /** the sanction does not follow the ladder from the Hub's standing */
  SANCTION_NOT_ALLOWED: 'SANCTION_NOT_ALLOWED',
  /** a suspended or terminated Hub cannot receive a distribution note */
  DESTINATION_INACTIVE: 'DESTINATION_INACTIVE',
  /** the invitation link is wrong, used or expired */
  INVITE_INVALID: 'INVITE_INVALID',
  CAPTCHA_FAILED: 'CAPTCHA_FAILED',
  /** only a Hub operator whose invitation is unused gets a new one */
  REINVITE_NOT_ALLOWED: 'REINVITE_NOT_ALLOWED',
  /** a submission repeated after the replay window: received (the message names its number), its code is no longer kept */
  HUB_REPLAY_EXPIRED: 'HUB_REPLAY_EXPIRED',
  // Households and orders (Phase 3), mirroring the backend's codes
  /** households and orders need the server's PII keys */
  HOUSEHOLD_UNAVAILABLE: 'HOUSEHOLD_UNAVAILABLE',
  /** the household's state does not allow this step */
  HOUSEHOLD_STATE: 'HOUSEHOLD_STATE',
  /** the order's state does not allow this step (someone else moved it on) */
  ORDER_STATE: 'ORDER_STATE',
  /** a submitToken already used for another request */
  SUBMIT_TOKEN_REUSED: 'SUBMIT_TOKEN_REUSED',
  /** the full cylinder cannot be handed out (where it is, its fill, status, retest, another order) */
  CYLINDER_NOT_EXCHANGEABLE: 'CYLINDER_NOT_EXCHANGEABLE',
  /** the empty cannot be taken (it is the full one, on a delivery note, destroyed, ...) */
  EMPTY_NOT_ACCEPTED: 'EMPTY_NOT_ACCEPTED',
  /** a wrong confirmation code */
  CONFIRMATION_INVALID: 'CONFIRMATION_INVALID',
  /** too many wrong codes: the order is locked for the courier, the Hub resolves */
  CONFIRMATION_LOCKED: 'CONFIRMATION_LOCKED',
  /** the order is not assigned to this courier */
  COURIER_NOT_ASSIGNED: 'COURIER_NOT_ASSIGNED',
  /** a courier works for one Hub: its location must be an active Hub */
  COURIER_HUB_ONLY: 'COURIER_HUB_ONLY',
  /** a CSV import that cannot be read (size, header, encoding) */
  IMPORT_INVALID: 'IMPORT_INVALID',
  /** an empty already received at the Hub */
  EMPTY_ALREADY_RECEIVED: 'EMPTY_ALREADY_RECEIVED',
  /** the household has an open order at this Hub (the counter: `detail.orderId`, complete it there) */
  ORDER_ALREADY_OPEN: 'ORDER_ALREADY_OPEN',
  /** the household has an open order at another Hub (`detail.orderId`): it is cancelled there first */
  ORDER_OPEN_ELSEWHERE: 'ORDER_OPEN_ELSEWHERE',
  /** the KK, the NIK belongs to another household / citizen already (counter registration) */
  KK_TAKEN: 'KK_TAKEN',
  NIK_TAKEN: 'NIK_TAKEN',
  /** the counter is a Hub operator's, at its own Hub */
  COUNTER_HUB_ONLY: 'COUNTER_HUB_ONLY',
  /** the household cancelled too many orders this month */
  CANCELLATION_LIMIT: 'CANCELLATION_LIMIT',
  /** the code is neither a serial nor an EPC, or names no cylinder in force (unknown, retired tag) */
  IDENTIFICATION_INVALID: 'IDENTIFICATION_INVALID',
  /** the household still holds a programme cylinder: it must be returned first (personal-data erasure) */
  HOUSEHOLD_HOLDS_CYLINDER: 'HOUSEHOLD_HOLDS_CYLINDER',
  /** the manufacturer's serial belongs to another cylinder */
  MANUFACTURER_SERIAL_TAKEN: 'MANUFACTURER_SERIAL_TAKEN',
  /** no way to send a sign-in code (WhatsApp not configured, or it failed) */
  OTP_UNAVAILABLE: 'OTP_UNAVAILABLE',
  /** the code is wrong, expired, used, or tried too often */
  OTP_INVALID: 'OTP_INVALID',
  OTP_RATE_LIMITED: 'OTP_RATE_LIMITED',
  OTP_UNDELIVERABLE: 'OTP_UNDELIVERABLE',
  // client side only
  /** no answer at all (offline, DNS, connection reset) */
  NETWORK: 'NETWORK',
  /** a 2xx answer that is not the envelope (a proxy page, an old backend) */
  BAD_RESPONSE: 'BAD_RESPONSE',
  /** the caller stopped a request itself (an upload cancelled, the page left) */
  ABORTED: 'ABORTED',
} as const;
export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];
